import { useState, useEffect } from 'react'
import { Play, MapPin, Flag, Bookmark, RotateCcw, XCircle, Minus, Moon, Hourglass, ListChecks } from 'lucide-react'
import { usePlans } from './TonightMode'
import { tmdbGet } from '../lib/tmdb'
import { airedSeasonCount, effectiveLastAired, nextProgressAfter, isEndedStatus } from '../lib/episode'
import { STATUS_LABELS } from '../hooks/useWatchlist'
import { supabase } from '../lib/supabase'
import { Sheet, BackButton, ActionRow, TONES } from './UI'
import { ClapboardClosed } from './ClapboardIcon'
import { endWatchSession } from '../lib/watchSessions'
import { logEvent } from '../lib/analytics'

// ── ShowStatusSheet (17) ───────────────────────────────────────────────────────
// One sheet for two moments: the beat right after a show is ADDED ("Where are you
// with it?") and changing a show's STATUS later (More → Status, My List hold, card ⋯).
// Replaces AddShowSpotlight (full-screen spotlight + ticket + confetti) and the 16-era
// QuickActionSheet status step. Flat rows, one serif title, the app's own tokens.
//
// Rows write exactly what the old flows wrote:
//   Start from the beginning → status watching + progress S1·E1 (pointer only, no activity)
//   I'm partway              → season pills → episode pills → watching + that pointer
//   Watched it all           → status completed (fires the completion ceremony itself)
//   Save for later           → status plan_to_watch (already the default after add)
//   Watch again              → status rewatching + progress S1·E1
//   Abandon                  → status dropped
//   Remove from list         → onRemove (caller confirms)

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }

// What a row does decides its colour: marking and "where" are amber (the app's verb),
// people are the social accent, a live signal is green, navigation stays neutral.
const ROW_TONES = {
  where: TONES.amber, mark: TONES.amber, start: TONES.amber, partway: TONES.amber, caught: TONES.amber, done: TONES.amber, again: TONES.amber,
  together: TONES.social, watchingNow: TONES.live, aftershow: TONES.social,
  later: TONES.muted, wait: TONES.muted, open: TONES.neutral, skip: TONES.muted, status: TONES.neutral, drop: TONES.danger, remove: TONES.danger,
}

export function ShowStatusSheet({ show, wl, mode = 'status', eyebrow, onClose, onRemove, onDone, extraRows = [] }) {
  // Whether the "Where are you with it?" ask actually appears after an add was
  // unobservable (2026-09-26: a Play guest added 86 shows and never set a position;
  // no way to tell if the sheet showed or was dismissed). One row per open.
  useEffect(() => { logEvent('status_sheet_shown', { mode, show: show?.id }) }, [mode, show?.id])
  const status = wl?.statuses?.[show?.id]
  const [step, setStep] = useState('rows')      // rows | partway
  const [seasons, setSeasons] = useState(null)   // [{season_number, episode_count}]
  const [season, setSeason] = useState(null)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState(null)   // { season, episode } awaiting the Tonight answer
  const plans = usePlans()

  useEffect(() => {
    if (step !== 'partway' || !show?.id || seasons) return
    let alive = true
    tmdbGet(`/tv/${show.id}`).then(d => {
      if (!alive) return
      const list = (d?.seasons || []).filter(s => s.season_number > 0)
      const n = airedSeasonCount(show) || list.length || 1
      const bounded = list.length ? list.filter(s => s.season_number <= Math.max(n, 1)) : Array.from({ length: n }, (_, i) => ({ season_number: i + 1, episode_count: 0 }))
      setSeasons(bounded); setSeason(bounded[0]?.season_number || 1)
    }).catch(() => { setSeasons([{ season_number: 1, episode_count: 0 }]); setSeason(1) })
    return () => { alive = false }
  }, [step, show, seasons])

  if (!show) return null

  const finish = (msg) => { onDone?.(msg); onClose?.() }
  const act = async (fn) => { if (busy) return; setBusy(true); try { await fn() } finally { setBusy(false) } }

  // Re-pointing a show's progress (from-the-beginning, pick-an-episode, rewatch)
  // passes watchedEp:null on purpose — none of these are a real "I watched this"
  // mark, so saveProgress's own endWatchSession call never fires (audit
  // 2026-09-20: a "watching now" session left open through a full position
  // reset kept every surface's icon lit for a show the viewer just rewound).
  // Close it explicitly here instead of teaching saveProgress a special case.
  const closeWatchingSession = async () => {
    const uid = wl?.userId || (await supabase.auth.getUser()).data?.user?.id
    if (uid) endWatchSession(uid, show.id)
  }
  // A just-added show gets one more beat after you've said where you are: put
  // that next episode on Tonight (Jason 2026-09-21: "when adding a new show...
  // prompt if they want to add at least the first episode to Tonight"). The
  // sheet stays open on a 'tonight' step instead of closing; either answer
  // closes it. Status mode (an existing show) is unchanged.
  const offerTonight = (season, episode, msg) => {
    if (mode === 'added' && plans?.toggle && wl?.userId) { onDone?.(msg); setPending({ season, episode }); setStep('tonight'); return true }
    return false
  }
  const startFromBeginning = () => act(async () => {
    await wl?.setStatus?.(show.id, 'watching')
    await wl?.saveProgress?.(show.id, { season: 1, episode: 1 }, { watchedEp: null, skipWrap: true })
    closeWatchingSession()
    if (!offerTonight(1, 1, 'From the beginning')) finish('From the beginning')
  })
  const pickEpisode = (ep) => act(async () => {
    await wl?.setStatus?.(show.id, 'watching')
    await wl?.saveProgress?.(show.id, { season, episode: ep }, { watchedEp: null, skipWrap: true })
    closeWatchingSession()
    if (!offerTonight(season, ep, `On S${season}·E${ep}`)) finish(`On S${season}·E${ep}`)
  })
  const putOnTonight = () => act(async () => {
    if (pending) await plans.toggle(show, pending.season, pending.episode, null)
    finish(`S${pending?.season}·E${pending?.episode} on Tonight`)
  })
  // "I'm caught up" (17.175): the door a /next-page arrival needs ("E10 airs tonight" →
  // I've seen E1–E9). Position = the next unaired episode, so the deck says "airs
  // tonight" instead of "start from the beginning". Only for a show still running.
  const todayStr = new Date().toISOString().slice(0, 10)
  const running = !isEndedStatus(show?.status)
  const nextEp = show?.next_episode_to_air
  const nextAir = nextEp?.air_date || null
  // Tonight's episode counts as "aired" for marking (effectiveLastAired), but someone
  // caught up at noon hasn't SEEN it: when next_episode_to_air is today or later, that
  // is the position. Otherwise, the episode after the last one that aired.
  const lastAired = running ? effectiveLastAired(show, todayStr) : null
  const caughtPos = !running ? null
    : (nextEp?.season_number >= 1 && nextEp?.episode_number >= 1 && nextAir && nextAir >= todayStr) ? { season: nextEp.season_number, episode: nextEp.episode_number }
    : lastAired ? nextProgressAfter({ season: lastAired.season_number ?? lastAired.season, episode: lastAired.episode_number ?? lastAired.episode }, show?.seasons) : null
  const caughtSub = caughtPos ? `Waiting for S${caughtPos.season}·E${caughtPos.episode}${nextAir === todayStr ? ' · airs tonight' : nextAir ? ` · ${new Date(nextAir + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}` : ''
  const caughtUp = () => act(async () => {
    await wl?.setStatus?.(show.id, 'watching')
    await wl?.saveProgress?.(show.id, caughtPos, { watchedEp: null, skipWrap: true })
    closeWatchingSession()
    logEvent('status_caught_up', { show: show.id, mode, src: 'status_sheet' })
    if (!(nextAir === todayStr && offerTonight(caughtPos.season, caughtPos.episode, 'Caught up'))) finish('Caught up')
  })
  const watchedAll = () => act(async () => { await wl?.setStatus?.(show.id, 'completed'); finish('Watched it all') })
  const saveForLater = () => act(async () => { if (status !== 'plan_to_watch') await wl?.setStatus?.(show.id, 'plan_to_watch'); finish('Saved for later') })
  const watchAgain = () => act(async () => {
    await wl?.setStatus?.(show.id, 'rewatching')
    await wl?.saveProgress?.(show.id, { season: 1, episode: 1 }, { watchedEp: null, skipWrap: true })
    closeWatchingSession()
    finish('Rewatching from S1·E1')
  })
  // Abandon keeps one honest line about why (the 16 AbandonSheet asked for a bail point +
  // bulbs; 17 asks one tap). Written to `abandonments`, best effort.
  const abandonWith = (note) => act(async () => {
    await wl?.setStatus?.(show.id, 'dropped')
    try {
      const prog = wl?.progress?.[show.id]
      const uid = wl?.userId || (await supabase.auth.getUser()).data?.user?.id
      if (uid) await supabase.from('abandonments').insert({ user_id: uid, show_id: show.id, show_name: show.name, show_poster: show.poster_path || null, bail_season: prog?.season || null, bail_episode: prog?.episode || null, note: note || null })
    } catch {}
    finish('Abandoned')
  })

  // Status rows (the seven ways to change where a show stands). In mode 'status' they
  // sit behind ONE row — "Change status · Watching" — so the ⋯ sheet shows five actions,
  // not twelve (Jason 2026-10-18); mode 'added' is only these rows, so it stays flat.
  const statusRows = [
    { key: 'start',    icon: Play,     label: 'Start from the beginning', sub: 'S1·E1 is next',                 on: status === 'watching' && wl?.progress?.[show.id]?.season === 1 && wl?.progress?.[show.id]?.episode === 1, go: startFromBeginning },
    // `on` used to be hardcoded false, so the single most common "watching" state
    // (mid-season, i.e. not exactly S1·E1) never showed as "Current" on ANY row
    // in this sheet (audit 2026-09-20). Its `on` is just the complement of
    // 'start'`s — watching, but not that S1·E1 pinned state.
    { key: 'partway',  icon: MapPin,   label: 'I’m partway through',       sub: 'Pick the episode you’re on',    on: status === 'watching' && !(wl?.progress?.[show.id]?.season === 1 && wl?.progress?.[show.id]?.episode === 1), go: () => setStep('partway') },
    ...(caughtPos ? [{ key: 'caught', icon: Hourglass, label: 'I’m caught up', sub: caughtSub, on: (status === 'watching' || status === 'caught_up') && wl?.progress?.[show.id]?.season === caughtPos.season && wl?.progress?.[show.id]?.episode === caughtPos.episode, go: caughtUp }] : []),
    { key: 'done',     icon: Flag,     label: 'Watched it all',            sub: 'Marks the show finished',       on: status === 'completed', go: watchedAll },
    { key: 'later',    icon: Bookmark, label: mode === 'added' ? 'Just save it' : 'Save for later', sub: 'Plan to watch', on: status === 'plan_to_watch', go: saveForLater },
    ...(mode === 'status' ? [
      { key: 'again',  icon: RotateCcw, label: 'Watch again',              sub: 'Rewatch from S1·E1',            on: status === 'rewatching', go: watchAgain },
      // Can't watch it yet (Jason 2026-10-18, Skinwalker Ranch: airing again but on no
      // service of his): parks the show off the deck without abandoning it. It walks
      // back on by itself the day it lands on one of your services (useUnpark).
      { key: 'wait',   icon: Hourglass, label: 'Can’t watch it yet',       sub: 'Not on your services. Off the deck, back the day it is', on: status === 'waiting', go: async () => { try { await wl?.setStatus?.(show.id, 'waiting') } catch {} ; finish('Parked until you can watch it') } },
      { key: 'drop',   icon: XCircle,   label: 'Abandon',                  sub: 'Keeps it in Finished, off the deck', on: status === 'dropped', go: () => setStep('abandon') },
      ...(onRemove ? [{ key: 'remove', icon: Minus, label: 'Remove from list', sub: null, dim: true, go: () => { onClose?.(); onRemove() } }] : []),
    ] : []),
  ]
  const current = statusRows.find(r => r.on)
  const rows = mode === 'status'
    ? [
        ...extraRows.map(r => ({ ...r, go: () => { onClose?.(); r.go?.() } })),
        { key: 'status', icon: ListChecks, label: 'Change status', sub: current ? `Now: ${current.label}` : 'Where you stand with it', go: () => setStep('status') },
      ]
    : statusRows

  const cur = seasons?.find(s => s.season_number === season)
  const epCount = cur?.episode_count || 0

  return (
    <Sheet open onClose={onClose} closeButton label={mode === 'added' ? 'Where are you with it?' : 'Status'} from="bottom" panelStyle={{ padding: '30px 20px calc(30px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        {show.poster_path && <img src={`https://image.tmdb.org/t/p/w92${show.poster_path}`} alt="" style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
        <div style={{ minWidth: 0 }}>
          <div style={EYE}>{eyebrow || (mode === 'added' ? 'Added to your list' : status ? STATUS_LABELS[status] : 'Status')}</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{show.name}</div>
        </div>
      </div>

      {step === 'rows' && (
        <div>
          {mode === 'added' && <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 6 }}>Where are you with it?</div>}
          {rows.map((r, i) => (
            <ActionRow key={r.key} index={i} onClick={r.go} disabled={busy} on={r.on} dim={r.dim} tone={r.tone || ROW_TONES[r.key] || TONES.neutral}
              icon={r.key === 'done' && r.on ? undefined : r.icon} iconNode={r.key === 'done' && r.on ? <ClapboardClosed size={16} color="var(--amber)" /> : undefined}
              label={r.label} sub={r.sub} />
          ))}
        </div>
      )}

      {step === 'status' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <BackButton onClick={() => setStep('rows')} size={30} />
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Where do you stand with it?</div>
          </div>
          {statusRows.map((r, i) => (
            <ActionRow key={r.key} index={i} onClick={r.go} disabled={busy} on={r.on} dim={r.dim} tone={r.tone || ROW_TONES[r.key] || TONES.neutral}
              icon={r.key === 'done' && r.on ? undefined : r.icon} iconNode={r.key === 'done' && r.on ? <ClapboardClosed size={16} color="var(--amber)" /> : undefined}
              label={r.label} sub={r.sub} />
          ))}
        </div>
      )}

      {step === 'tonight' && pending && (
        <div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.2 }}>S{pending.season}·E{pending.episode} tonight?</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>Put it on tonight’s list and it’s waiting when you sit down. The moon lights up when it’s time.</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={() => finish()} disabled={busy} className="ui-hit" style={{ flex: 1, height: 44, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Not tonight</button>
            <button onClick={putOnTonight} disabled={busy} className="ui-hit cta-glow" style={{ flex: 1.3, height: 44, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><Moon size={15} fill="currentColor" />Put it on Tonight</button>
          </div>
        </div>
      )}
      {step === 'abandon' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <BackButton onClick={() => setStep('rows')} size={30} />
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Where did it lose you?</div>
          </div>
          {['Lost interest', 'Too slow', 'Went somewhere I didn’t like', 'No time right now', 'Just not for me'].map(r => (
            <button key={r} onClick={() => abandonWith(r)} disabled={busy} style={{ width: '100%', minHeight: 48, display: 'flex', alignItems: 'center', background: 'none', border: 'none', borderBottom: '1px solid var(--border-soft, var(--border))', padding: '8px 0', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 15, color: 'var(--text)', textAlign: 'left' }}>{r}</button>
          ))}
          <button onClick={() => abandonWith(null)} disabled={busy} style={{ marginTop: 4, minHeight: 44, background: 'none', border: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer', padding: '6px 0' }}>Skip, just abandon it</button>
        </div>
      )}
      {step === 'partway' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <BackButton onClick={() => setStep('rows')} size={30} />
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Which episode are you on next?</div>
          </div>
          {!seasons && <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '10px 0' }}>Loading seasons…</div>}
          {seasons && (
            <>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
                {seasons.map(s => {
                  const on = s.season_number === season
                  return <button key={s.season_number} onClick={() => setSeason(s.season_number)} className="ui-hit" style={{ flexShrink: 0, height: 36, minWidth: 44, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: `1px solid ${on ? 'var(--amber)' : 'var(--border)'}`, background: on ? 'var(--amber)' : 'var(--bg-card)', color: on ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>S{s.season_number}</button>
                })}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))', gap: 8, marginTop: 14 }}>
                {Array.from({ length: epCount || 12 }, (_, i) => i + 1).map(ep => (
                  <button key={ep} onClick={() => pickEpisode(ep)} disabled={busy} className="ui-hit" style={{ height: 40, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 13, cursor: 'pointer' }}>E{ep}</button>
                ))}
              </div>
              {!epCount && <div style={{ color: 'var(--text-dim)', fontSize: 12.5, marginTop: 10 }}>Episode count unknown for this season, pick the number you’re on.</div>}
            </>
          )}
        </div>
      )}
    </Sheet>
  )
}

// Listens for a show being added anywhere in the app and opens the sheet in
// "added" mode. Mounted once in CinematicLayer, where AddShowSpotlight used to be.
export function ShowAddedSheet({ wl }) {
  const [show, setShow] = useState(null)
  useEffect(() => {
    const on = (e) => {
      // The Welcome add step owns the screen while __nextepOnboarding is set; a picker
      // that wants the same quiet can set __nextepPickerActive.
      if (window.__nextepPickerActive || window.__nextepOnboarding) return
      const s = e.detail?.show || e.detail
      // A ring tap added this show and already set the position (ShowPage17 pendingMark).
      if (s?.id && window.__nextepMarkOnAdd === s.id) { delete window.__nextepMarkOnAdd; return }
      if (s?.id) setShow(s)
    }
    window.addEventListener('nextep:show_added', on)
    return () => window.removeEventListener('nextep:show_added', on)
  }, [])
  if (!show) return null
  return <ShowStatusSheet show={show} wl={wl} mode="added" onClose={() => setShow(null)} />
}
